'use strict';

/**
 * Authentication UI management and interactions
 * Handles DOM updates, overlays, page protection, and visual feedback
 */
window.SoracomAuthUI = (() => {

  // Get auth config from central location
  const getAuthConfig = () => window.SoracomAuth?.getAuthConfig?.();

  // Anti-flash gating: store elements we temporarily hide until auth resolved
  let resourcesBlocked = false;
  let tocInitialized = false;

  /**
   * Update page protection based on authentication state
   * @param {Object|null} auth - Authentication data
   */
  function updatePageProtection(auth) {
    const overlay = document.getElementById('auth-overlay');
    const authenticatedContent = document.getElementById('authenticated-content');
    const signInBanner = document.querySelector('.sign-in-required-banner');

    const authOverlay = document.querySelector('.auth-overlay');
    const authRequired = document.querySelector('.auth-required');
    const gatedContent = document.querySelector('.auth-gated-content');

    if (overlay && authenticatedContent) {
      if (auth) {
        overlay.style.setProperty('display', 'none', 'important');
        authenticatedContent.classList.add('auth-confirmed');
        authenticatedContent.classList.remove('prevent-resource-loading');
        authenticatedContent.style.setProperty('display', 'block', 'important');

        hideAuthOverlay();
        restoreResourceLoading();

        const toc = document.getElementById('toc');
        if (toc && !tocInitialized) {
          tocInitialized = true;
          // Only show TOC if there are visible headers after authentication
          if (typeof $ !== 'undefined' && $('#authenticated-content h2:visible').length) {
            toc.classList.remove('hidden');
            toc.style.display = '';
          }
          if (typeof $ !== 'undefined' && typeof $.fn !== 'undefined' && $.fn.tocify && $('#authenticated-content h2:visible').length) {
            const initTOC = () => {
              try {
                const hasGuideClass = document.body && document.body.classList.contains('guide');
                $('#toc-content').tocify({
                  showAndHide: hasGuideClass,
                  hashGenerator: 'pretty',
                  context: '#authenticated-content',
                  selectors: 'h2,h3,h4',
                  showEffect: 'slideDown',
                  showEffectSpeed: 'fast',
                  hideEffect: 'slideUp',
                  hideEffectSpeed: 'fast',
                  theme: 'none',
                  scrollTo: 80
                });
              } catch (e) {
                console.warn('TOC initialization failed:', window.SoracomAuthUtils?.sanitizeForLogging?.(e?.message || e) || '[error details unavailable]');
              }
            };

            if (window.requestIdleCallback) {
              requestIdleCallback(initTOC, { timeout: 500 });
            } else {
              setTimeout(initTOC, 100);
            }
          }
        }
      } else {
        authenticatedContent.classList.remove('auth-confirmed');
        authenticatedContent.style.setProperty('display', 'none', 'important');
        overlay.style.setProperty('display', 'flex', 'important');
      }
    } else if (authenticatedContent && !overlay) {
      if (auth) {
        authenticatedContent.classList.add('auth-confirmed');
        authenticatedContent.classList.remove('prevent-resource-loading');
        authenticatedContent.style.setProperty('display', 'block', 'important');
        restoreResourceLoading();

        if (signInBanner) {
          signInBanner.style.setProperty('display', 'none', 'important');
        }
      } else {
        authenticatedContent.classList.remove('auth-confirmed');
        authenticatedContent.style.setProperty('display', 'none', 'important');

        if (signInBanner) {
          signInBanner.style.setProperty('display', 'block', 'important');
        } else {
          showAuthOverlay();
        }
      }
    } else if (signInBanner && !authenticatedContent && !overlay) {
      if (auth) {
        signInBanner.style.setProperty('display', 'none', 'important');
      } else {
        signInBanner.style.setProperty('display', 'block', 'important');
      }
    } else {
      if (authOverlay) {
        if (auth) {
          authOverlay.style.setProperty('display', 'none', 'important');
        } else {
          authOverlay.style.setProperty('display', 'flex', 'important');
        }
      }

      if (authRequired) {
        if (auth) {
          authRequired.style.setProperty('display', 'block', 'important');
        } else {
          authRequired.style.setProperty('display', 'none', 'important');
        }
      }

      if (gatedContent) {
        if (auth) {
          gatedContent.style.setProperty('display', 'block', 'important');
        } else {
          gatedContent.style.setProperty('display', 'none', 'important');
        }
      }
    }

    if (auth) {
      document.body.classList.add('authenticated');
    } else {
      document.body.classList.remove('authenticated');
    }

    restoreResourceLoading();

    if (auth && auth.coverage) {
      window.SoracomAuthCoverage?.updateCoverageUI(auth.coverage);
    }

    if (overlay) {
      overlay.classList.add('auth-overlay-hidden');
    }
  }

  /**
   * Update sign-out button visibility
   * @param {Object|null} auth - Authentication data
   */
  function updateSignOutVisibility(auth) {
    if (window.signingOut) {
      return;
    }

    const signOutButton = document.getElementById('toolbar-sign-out');

    if (signOutButton) {
      const listItem = signOutButton.closest('li');
      if (listItem) {
        if (auth && (auth.token || auth.apiKey)) {
          listItem.classList.add('show');
        } else {
          listItem.classList.remove('show');
        }
      }
    }
  }

  /**
   * Show authentication overlay
   */
  function showAuthOverlay() {
    let overlay = document.getElementById('auth-overlay');
    if (!overlay) {
      createAuthOverlay();
      overlay = document.getElementById('auth-overlay');
    }
    overlay.style.display = 'flex';
    overlay.classList.add('active');

    const banner = overlay.querySelector('.sign-in-required-banner');
    if (banner) {
      banner.style.display = '';
    }
  }

  /**
   * Create authentication overlay element
   */
  function createAuthOverlay() {
    const overlay = document.createElement('div');
    overlay.id = 'auth-overlay';
    overlay.className = 'auth-overlay';

    const content = document.createElement('div');
    content.className = 'auth-overlay-content';

    const heading = document.createElement('h3');
    heading.textContent = 'Authentication Required';
    heading.className = 'auth-overlay-heading';

    const description = document.createElement('p');
    description.textContent = 'This page requires authentication to view. Please sign in to continue.';
    description.style.marginBottom = '1.5rem';
    description.style.color = '#666';
    description.style.lineHeight = '1.5';

    const signinButton = document.createElement('button');
    signinButton.id = 'overlay-sso-signin';
    signinButton.className = 'auth-primary-button';
    signinButton.textContent = 'Sign In with SSO';

    const divider = document.createElement('div');
    divider.textContent = 'or';
    divider.className = 'divider';

    const authKeysInfo = document.createElement('p');
    authKeysInfo.style.color = '#888';
    const small = document.createElement('small');
    small.textContent = 'Use your AuthKeys to authenticate via the API reference.';
    authKeysInfo.appendChild(small);

    content.appendChild(heading);
    content.appendChild(description);
    content.appendChild(signinButton);
    content.appendChild(divider);
    content.appendChild(authKeysInfo);

    overlay.appendChild(content);
    document.body.appendChild(overlay);

    signinButton.addEventListener('click', (e) => {
      const button = e.target;
      const originalText = button.textContent;
      const originalBgColor = button.style.backgroundColor;

      button.textContent = 'Redirecting...';
      button.disabled = true;
      button.style.backgroundColor = '#6c757d';
      button.style.cursor = 'not-allowed';

      const resetButton = () => {
        button.textContent = originalText;
        button.disabled = false;
        button.style.backgroundColor = originalBgColor || '#007bff';
        button.style.cursor = 'pointer';
      };

      const failsafeTimeout = setTimeout(resetButton, 10000);

      const currentUrl = window.location.href;
      const authConfig = getAuthConfig();
      if (!authConfig) {
        console.error('Auth configuration not available');
        resetButton();
        return;
      }
      const redirectUrl = `${authConfig.BASE_URL}${authConfig.ENDPOINTS.LOGIN}?redirect=${encodeURIComponent(currentUrl)}`;

      sessionStorage.setItem('auth_redirect', 'true');

      try {
        setTimeout(() => {
          clearTimeout(failsafeTimeout);
          window.location.href = redirectUrl;
        }, 500);
      } catch (error) {
        console.error('Redirect failed:', window.SoracomAuthUtils?.sanitizeForLogging?.(error?.message || error) || '[error details unavailable]');
        resetButton();
      }
    });
  }

  /**
   * Hide authentication overlay
   */
  function hideAuthOverlay() {
    const overlay = document.getElementById('auth-overlay');
    if (overlay) {
      overlay.classList.add('auth-overlay-hidden');
    }
  }

  /**
   * Prevent resource loading on auth-gated pages
   */
  function preventResourceLoading() {
    // Don't try to prevent resource loading if the document isn't ready yet
    if (!document.body) {
      return;
    }

    const authGatedContainers = document.querySelectorAll('[data-auth-required], .auth-required-content, .protected-content');

    if (authGatedContainers.length === 0) {
      const isPageAuthGated = document.querySelector('#sandbox-authentication') ||
        document.body.classList.contains('auth-required');

      if (isPageAuthGated) {
        const contentArea = document.querySelector('.content-wrapper, .page-content, main, #content') ||
          document.body;
        if (contentArea) {
          blockResourcesInContainer(contentArea);
        }
      }
    } else {
      authGatedContainers.forEach(container => {
        blockResourcesInContainer(container);
        container.style.display = 'none';
        container.classList.add('auth-hidden');
      });
    }

    resourcesBlocked = true;

    if (window.SoracomAuth?.getAuthState()) {
      setTimeout(restoreResourceLoading, 0);
    }
  }

  /**
   * Block resources in container
   * @param {HTMLElement} container - Container element
   */
  function blockResourcesInContainer(container) {
    const mediaSelectors = [
      'img[src]:not([src^="data:"]):not([src^="#"])',
      'video[src]:not([src^="data:"]):not([src^="#"])',
      'audio[src]:not([src^="data:"]):not([src^="#"])',
      'embed[src]:not([src^="data:"]):not([src^="#"])',
      'object[data]:not([data^="data:"]):not([data^="#"])',
      'source[src]:not([src^="data:"]):not([src^="#"])',
      'track[src]:not([src^="data:"]):not([src^="#"])'
    ];

    mediaSelectors.forEach(selector => {
      const elements = container.querySelectorAll(selector);
      elements.forEach(element => {
        if (element.src) {
          element.dataset.originalSrc = element.src;
          element.removeAttribute('src');
        }
        if (element.data) {
          element.dataset.originalData = element.data;
          element.removeAttribute('data');
        }
        element.classList.add('auth-blocked-resource');
      });
    });
  }

  /**
   * Restore resource loading
   */
  function restoreResourceLoading() {
    const blockedElements = document.querySelectorAll('.auth-blocked-resource');
    blockedElements.forEach(element => {
      if (element.dataset.originalSrc) {
        element.src = element.dataset.originalSrc;
        delete element.dataset.originalSrc;
      }
      if (element.dataset.originalData) {
        element.data = element.dataset.originalData;
        delete element.dataset.originalData;
      }
      element.classList.remove('auth-blocked-resource');
    });

    const authGatedContainers = window.SoracomAuthUtils?.DOM_CACHE.getAuthGatedContainers();
    if (authGatedContainers) {
      authGatedContainers.forEach(container => {
        container.style.display = '';
        container.classList.remove('auth-hidden');
      });
    }
  }

  // Public API
  return {
    updatePageProtection,
    updateSignOutVisibility,
    showAuthOverlay,
    hideAuthOverlay,
    preventResourceLoading,
    restoreResourceLoading,
    resourcesBlocked
  };

})();